import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaCrown, FaMedal, FaStar, FaFire, FaUsers, FaSpinner, FaSync, FaGem, FaBolt, FaShieldAlt, FaArrowLeft, FaRocket, FaAtom, FaLightbulb, FaGlobe, FaChartLine, FaAward, FaBullseye } from 'react-icons/fa';
import { getLeaderboard, getUserRank, type LeaderboardEntry as RawLeaderboardEntry } from '../lib/challenges';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';

/** Normalized shape the UI expects (camelCase). */
type UILeader = {
  userId: string;
  rank: number;
  profileImage?: string | null;
  fullName?: string | null;
  email?: string;
  lastActivity?: string | null;
  totalPoints: number;
  currentStreak: number;
  challengesCompleted: number;
};

/** Normalize backend entry: accept snake_case or camelCase safely. */
function normalizeEntry(e: any): UILeader | null {
  if (!e) return null;

  const userId =
    e.userId ??
    e.user_id ??
    e.id ??
    (e.user && (e.user.id ?? e.user.user_id)) ??
    '';

  if (!userId) return null;

  const profileImage =
    e.profileImage ??
    e.profile_image ??
    e.user?.profile_image ??
    e.user?.profile_image_url ??
    null;

  const fullName =
    e.fullName ??
    e.full_name ??
    e.user?.full_name ??
    null;

  const email =
    e.email ??
    e.user?.email ??
    undefined;

  const rank = Number(e.rank ?? e.position ?? 0);
  const lastActivity = e.lastActivity ?? e.last_activity ?? null;
  const totalPoints = Number(e.totalPoints ?? e.total_points ?? e.points ?? 0);
  const currentStreak = Number(e.currentStreak ?? e.current_streak ?? e.streak ?? 0);
  const challengesCompleted = Number(e.challengesCompleted ?? e.challenges_completed ?? e.completed ?? 0);

  return {
    userId,
    rank: isNaN(rank) ? 0 : rank,
    profileImage,
    fullName,
    email,
    lastActivity,
    totalPoints: isNaN(totalPoints) ? 0 : totalPoints,
    currentStreak: isNaN(currentStreak) ? 0 : currentStreak,
    challengesCompleted: isNaN(challengesCompleted) ? 0 : challengesCompleted,
  };
}

/** Safe date distance label */
function formatLastActivity(dateString?: string | null) {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return '—';
  const diffInHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffInHours < 1) return 'Hace <1h';
  if (diffInHours < 24) return `Hace ${diffInHours}h`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `Hace ${diffInDays}d`;
}

export default function LeaderboardPage() {
  const [leaders, setLeaders] = useState<UILeader[]>([]);
  const [userRank, setUserRank] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'week' | 'month'>('all');
  const [showStats, setShowStats] = useState(false);

  const rawUser = getCurrentUser();
  const user = React.useMemo(() => {
    if (!rawUser) return null;
    const { id, email, profile_image } = rawUser as any;
    return { id, email, profile_image };
  }, [rawUser?.id, rawUser?.email, (rawUser as any)?.profile_image]);

  const lastErrorRef = useRef<number>(0);
  const TOAST_COOLDOWN_MS = 2000;
  const navigate = useNavigate();

  const showErrorToastOnce = useCallback((msg: string) => {
    const now = Date.now();
    if (now - lastErrorRef.current > TOAST_COOLDOWN_MS) {
      lastErrorRef.current = now;
      toast.error(msg);
    }
  }, []);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <motion.div
            className="relative"
            animate={{ 
              rotate: [0, 5, -5, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FaCrown className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-yellow-400 drop-shadow-lg" />
            <motion.div
              className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-400 rounded-full"
              animate={{ 
                scale: [0, 1.2, 0],
                opacity: [0, 1, 0]
              }}
              transition={{ 
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            animate={{ 
              y: [0, -2, 0],
              rotate: [0, 2, -2, 0]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FaMedal className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-gray-300 drop-shadow-lg" />
          </motion.div>
        );
      case 3:
        return (
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              rotate: [0, -3, 3, 0]
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FaMedal className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-orange-400 drop-shadow-lg" />
          </motion.div>
        );
      default:
        return (
          <div className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border border-gray-600 flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-xs sm:text-sm lg:text-base">#{rank}</span>
          </div>
        );
    }
  };

  const getRankBackground = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/30 via-yellow-400/20 to-amber-500/30 border-yellow-400/60 shadow-lg';
      case 2:
        return 'bg-gradient-to-r from-gray-400/30 via-gray-300/20 to-slate-400/30 border-gray-300/60 shadow-lg';
      case 3:
        return 'bg-gradient-to-r from-orange-500/30 via-orange-400/20 to-amber-600/30 border-orange-400/60 shadow-lg';
      default:
        return 'bg-gradient-to-r from-gray-800/40 via-gray-900/30 to-black/40 border-gray-600/40 hover:border-gray-500/60';
    }
  };

  const loadLeaderboard = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const data = await getLeaderboard(10); // Load only top 10 users
      if (signal?.aborted) return;
      const normalized: UILeader[] = Array.isArray(data)
        ? data.map(normalizeEntry).filter(Boolean) as UILeader[]
        : [];
      const withRank = normalized
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .slice(0, 10) // Ensure only top 10
        .map((e, i) => ({ ...e, rank: e.rank && e.rank > 0 ? e.rank : i + 1 }));

      setLeaders(withRank);
    } catch (error: any) {
      console.error('Error loading leaderboard:', error);
      showErrorToastOnce('Failed to load leaderboard');
      setLeaders([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [showErrorToastOnce]);

  const loadUserRank = useCallback(async (signal?: AbortSignal) => {
    if (!user) return;
    try {
      const rank = await getUserRank(user.id);
      if (signal?.aborted) return;
      setUserRank(rank ?? null);
    } catch (error) {
      console.error('Error loading user rank:', error);
    }
  }, [user]);

  useEffect(() => {
    const controller = new AbortController();
    loadLeaderboard(controller.signal);
    loadUserRank(controller.signal);
    return () => controller.abort();
  }, [loadLeaderboard, loadUserRank]);

  const handleRefresh = async () => {
    setRefreshing(true);
    const controller = new AbortController();
    try {
      await Promise.all([
        loadLeaderboard(controller.signal),
        loadUserRank(controller.signal),
      ]);
      toast.success('Leaderboard actualizado');
    } catch {
      showErrorToastOnce('Error al actualizar');
    } finally {
      setRefreshing(false);
      controller.abort();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white relative overflow-hidden overflow-x-hidden">
      {/* Epic Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated Orbs */}
        <motion.div 
          className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-br from-yellow-400/20 to-amber-500/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute -bottom-20 -right-20 w-60 h-60 bg-gradient-to-br from-purple-400/15 to-pink-400/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
            x: [0, -40, 0],
            y: [0, 20, 0]
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/4 w-32 h-32 bg-gradient-to-br from-blue-400/15 to-cyan-400/10 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2],
            rotate: [0, 180, 360]
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        {/* Floating Particles */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-yellow-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      </div>

      {/* Header Section */}
      <div className="relative z-10 pt-8 pb-6">
        <div className="w-full px-4 sm:px-6">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/dashboard"
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-lg rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
            >
              <FaArrowLeft className="w-4 h-4" />
              <span className="font-medium">Dashboard</span>
            </Link>

            <motion.button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-400/20 to-amber-500/20 backdrop-blur-lg rounded-full border border-yellow-400/30 hover:border-yellow-400/50 transition-all duration-300 disabled:opacity-50"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaSync className={`w-4 h-4 text-yellow-400 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-yellow-400 font-medium">Actualizar</span>
            </motion.button>
          </div>

          {/* Epic Header */}
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600 rounded-full mb-6 shadow-2xl"
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FaTrophy className="w-10 h-10 text-black drop-shadow-lg" />
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-yellow-400/60"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.6, 0.2, 0.6]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>

            <motion.h1 
              className="text-2xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-yellow-200 to-white bg-clip-text text-transparent mb-2 sm:mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              🏆 Top 10 Elite
            </motion.h1>
            
            <motion.p 
              className="text-sm sm:text-lg text-gray-300 max-w-2xl mx-auto px-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Los 10 mejores estudiantes de español del mundo
            </motion.p>

            {/* Stats Overview */}
            <motion.div 
              className="mt-4 sm:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 max-w-4xl mx-auto px-2"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/20">
                <FaUsers className="w-4 h-4 sm:w-6 sm:h-6 text-blue-400 mx-auto mb-1 sm:mb-2" />
                <div className="text-lg sm:text-2xl font-bold text-white">{leaders.length}</div>
                <div className="text-xs text-gray-400">Estudiantes</div>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/20">
                <FaFire className="w-4 h-4 sm:w-6 sm:h-6 text-orange-400 mx-auto mb-1 sm:mb-2" />
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {leaders.reduce((sum, l) => sum + l.currentStreak, 0)}
                </div>
                <div className="text-xs text-gray-400">Racha Total</div>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/20">
                <FaShieldAlt className="w-4 h-4 sm:w-6 sm:h-6 text-green-400 mx-auto mb-1 sm:mb-2" />
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {leaders.reduce((sum, l) => sum + l.challengesCompleted, 0)}
                </div>
                <div className="text-xs text-gray-400">Desafíos</div>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/20">
                <FaGem className="w-4 h-4 sm:w-6 sm:h-6 text-purple-400 mx-auto mb-1 sm:mb-2" />
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {leaders.reduce((sum, l) => sum + l.totalPoints, 0)}
                </div>
                <div className="text-xs text-gray-400">Puntos</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* User Rank Section */}
      {user && userRank && (
        <motion.div 
          className="relative z-10 w-full px-4 sm:px-6 mb-4 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="bg-black/90 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-gray-700/50 shadow-2xl">
            <div className="space-y-3 sm:space-y-4">
              
              {/* User Info */}
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="relative">
                  <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl overflow-hidden bg-gray-800 p-1 shadow-xl border border-gray-600">
                    <div className="w-full h-full rounded-lg sm:rounded-xl overflow-hidden">
                      {user.profile_image ? (
                        <img src={user.profile_image} alt="Tu perfil" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                          <span className="text-white font-bold text-xs sm:text-lg">{(user.email || '?').charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <motion.div
                    className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-6 sm:h-6 bg-green-400 rounded-full border-2 border-gray-800 shadow-lg flex items-center justify-center"
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0.8, 1, 0.8]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <FaBolt className="w-1.5 h-1.5 sm:w-3 sm:h-3 text-black" />
                  </motion.div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm sm:text-xl mb-1">
                    Tu Posición: 
                    <span className="ml-1 sm:ml-2 text-lg sm:text-2xl text-yellow-400">
                      #{userRank.rank ?? '—'}
                    </span>
                  </div>
                  <div className="text-gray-300 font-medium text-xs sm:text-base">
                    {userRank.totalPoints ?? 0} puntos totales
                  </div>
                </div>
              </div>
              
              {/* User Stats */}
              <div className="grid grid-cols-3 gap-1 sm:gap-4">
                <motion.div 
                  className="text-center bg-gray-800/50 rounded-md sm:rounded-xl p-1.5 sm:p-3 border border-gray-600/30"
                  whileHover={{ scale: 1.05 }}
                >
                  <FaFire className="w-3 h-3 sm:w-6 sm:h-6 text-orange-400 mx-auto mb-1" />
                  <div className="text-white font-bold text-sm sm:text-lg">{userRank.currentStreak ?? 0}</div>
                  <div className="text-gray-400 text-xs sm:text-sm">Racha</div>
                </motion.div>
                
                <motion.div 
                  className="text-center bg-gray-800/50 rounded-md sm:rounded-xl p-1.5 sm:p-3 border border-gray-600/30"
                  whileHover={{ scale: 1.05 }}
                >
                  <FaShieldAlt className="w-3 h-3 sm:w-6 sm:h-6 text-blue-400 mx-auto mb-1" />
                  <div className="text-white font-bold text-sm sm:text-lg">{userRank.challengesCompleted ?? 0}</div>
                  <div className="text-gray-400 text-xs sm:text-sm">Desafíos</div>
                </motion.div>

                <motion.div 
                  className="text-center bg-gray-800/50 rounded-md sm:rounded-xl p-1.5 sm:p-3 border border-gray-600/30"
                  whileHover={{ scale: 1.05 }}
                >
                  <FaBullseye className="w-3 h-3 sm:w-6 sm:h-6 text-green-400 mx-auto mb-1" />
                  <div className="text-white font-bold text-sm sm:text-lg">{Math.round((userRank.challengesCompleted / Math.max(userRank.totalPoints / 10, 1)) * 100) || 0}%</div>
                  <div className="text-gray-400 text-xs sm:text-sm">Precisión</div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Leaderboard */}
      <div className="relative z-10 w-full px-4 sm:px-6 pb-8">
        <div className="bg-black/60 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden">
          
          {/* Leaderboard Header */}
          <div className="bg-gradient-to-r from-yellow-400/20 via-amber-500/15 to-yellow-600/20 p-3 sm:p-6 border-b border-gray-700/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  <FaRocket className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400" />
                </motion.div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold text-white">Top 10 Estudiantes</h2>
                  <p className="text-gray-300 text-sm sm:text-base">Los mejores del mundo</p>
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex space-x-1 sm:space-x-2">
                {[
                  { key: 'all', label: 'Todo', icon: FaGlobe },
                  { key: 'week', label: 'Semana', icon: FaChartLine },
                  { key: 'month', label: 'Mes', icon: FaAward }
                ].map(({ key, label, icon: Icon }) => (
                  <motion.button
                    key={key}
                    onClick={() => setSelectedFilter(key as any)}
                    className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full border transition-all duration-300 text-xs sm:text-sm ${
                      selectedFilter === key
                        ? 'bg-yellow-400 text-black border-yellow-400'
                        : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="font-medium">{label}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>

          {/* Leaderboard Content */}
          <div className="p-3 sm:p-6">
            {loading ? (
              <div className="text-center py-8 sm:py-12">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6"
                >
                  <div className="w-full h-full rounded-full border-4 border-gray-700 border-t-yellow-400 shadow-lg"></div>
                </motion.div>
                <p className="text-gray-300 text-sm sm:text-lg font-medium">Cargando clasificación épica...</p>
              </div>
            ) : leaders.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <FaUsers className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mx-auto mb-4" />
                </motion.div>
                <p className="text-gray-400 text-sm sm:text-base">¡Sé el primero en completar un desafío para aparecer en el Top 10!</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                <AnimatePresence>
                  {leaders.map((leader, index) => (
                    <motion.div
                      key={leader.userId}
                      className={`relative overflow-hidden flex items-center p-2 sm:p-4 lg:p-6 rounded-lg sm:rounded-xl border-2 transition-all duration-500 hover:scale-[1.02] ${getRankBackground(leader.rank)}`}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ 
                        duration: 0.6, 
                        delay: index * 0.05,
                        type: "spring",
                        stiffness: 100
                      }}
                      whileHover={{
                        scale: 1.02,
                        transition: { duration: 0.2 }
                      }}
                    >
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-bl from-white/20 to-transparent rounded-full blur-xl"></div>
                      </div>
                      
                      {/* Rank Icon */}
                      <div className="flex-shrink-0 mr-2 sm:mr-4">
                        {getRankIcon(leader.rank)}
                      </div>

                      {/* User Avatar */}
                      <div className="relative flex-shrink-0 mr-2 sm:mr-4">
                        <div className={`w-8 h-8 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl overflow-hidden ${
                          leader.rank <= 3 ? 'p-0.5 sm:p-1 bg-gradient-to-br from-yellow-400 to-amber-500' : 'bg-gray-700'
                        } shadow-lg`}>
                          <div className="w-full h-full rounded-lg sm:rounded-xl overflow-hidden">
                            {leader.profileImage ? (
                              <img
                                src={leader.profileImage}
                                alt={leader.fullName ?? leader.email ?? 'User'}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                                <span className="text-white font-bold text-xs sm:text-lg">
                                  {((leader.fullName || leader.email || 'U').charAt(0) || '?').toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {leader.rank <= 3 && (
                          <motion.div
                            className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 w-4 h-4 sm:w-6 sm:h-6 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg"
                            animate={{ 
                              rotate: [0, 360],
                              scale: [1, 1.1, 1]
                            }}
                            transition={{ 
                              duration: 3,
                              repeat: Infinity,
                              ease: "linear"
                            }}
                          >
                            <FaGem className="w-2 h-2 sm:w-3 sm:h-3 text-black" />
                          </motion.div>
                        )}
                      </div>
                      
                      {/* User Info */}
                      <div className="flex-1 min-w-0 mr-2 sm:mr-4">
                        {leader.fullName ? (
                          <>
                            <div className={`font-bold text-sm sm:text-lg lg:text-xl mb-0.5 sm:mb-1 truncate ${
                              leader.rank === 1 ? 'text-yellow-300' :
                              leader.rank === 2 ? 'text-gray-200' :
                              leader.rank === 3 ? 'text-orange-300' :
                              'text-white'
                            }`}>
                              {leader.fullName}
                            </div>
                            <div className="text-gray-400 font-medium text-xs sm:text-sm">
                              {formatLastActivity(leader.lastActivity)}
                            </div>
                          </>
                        ) : (
                          <div className="text-gray-400 font-medium text-xs sm:text-sm">
                            {formatLastActivity(leader.lastActivity)}
                          </div>
                        )}
                      </div>

                      {/* Points */}
                      <div className="flex-shrink-0 text-right mr-2 sm:mr-4">
                        <div className={`text-lg sm:text-xl lg:text-2xl font-bold mb-0.5 sm:mb-1 ${
                          leader.rank === 1 ? 'text-yellow-400' :
                          leader.rank === 2 ? 'text-gray-300' :
                          leader.rank === 3 ? 'text-orange-400' :
                          'text-white'
                        }`}>
                          {leader.totalPoints}
                        </div>
                        <div className="text-gray-400 text-xs sm:text-sm font-medium">pts</div>
                      </div>

                      {/* Stats - Hidden on very small screens, shown on larger */}
                      <div className="hidden sm:flex items-center space-x-2 lg:space-x-3">
                        <motion.div 
                          className="flex items-center space-x-1 bg-orange-500/20 rounded-lg px-2 lg:px-3 py-1 lg:py-2 border border-orange-400/30"
                          whileHover={{ scale: 1.05 }}
                        >
                          <FaFire className="w-3 h-3 lg:w-4 lg:h-4 text-orange-400" />
                          <span className="text-white font-bold text-xs lg:text-sm">{leader.currentStreak}</span>
                        </motion.div>
                        
                        <motion.div 
                          className="flex items-center space-x-1 bg-blue-500/20 rounded-lg px-2 lg:px-3 py-1 lg:py-2 border border-blue-400/30"
                          whileHover={{ scale: 1.05 }}
                        >
                          <FaStar className="w-3 h-3 lg:w-4 lg:h-4 text-blue-400" />
                          <span className="text-white font-bold text-xs lg:text-sm">{leader.challengesCompleted}</span>
                        </motion.div>
                      </div>

                      {/* Mobile Stats - Compact version for small screens */}
                      <div className="sm:hidden flex flex-col items-center space-y-1">
                        <div className="flex items-center space-x-1 bg-orange-500/20 rounded px-1.5 py-0.5 border border-orange-400/30">
                          <FaFire className="w-2.5 h-2.5 text-orange-400" />
                          <span className="text-white font-bold text-xs">{leader.currentStreak}</span>
                        </div>
                        <div className="flex items-center space-x-1 bg-blue-500/20 rounded px-1.5 py-0.5 border border-blue-400/30">
                          <FaStar className="w-2.5 h-2.5 text-blue-400" />
                          <span className="text-white font-bold text-xs">{leader.challengesCompleted}</span>
                        </div>
                      </div>

                      {/* Top 3 Position Indicator */}
                      {leader.rank <= 3 && (
                        <motion.div
                          className="absolute top-1 right-1 sm:top-2 sm:right-2"
                          animate={{ 
                            scale: [1, 1.1, 1],
                            opacity: [0.7, 1, 0.7]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <div className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs font-bold ${
                            leader.rank === 1 ? 'bg-yellow-400/30 text-yellow-200 border border-yellow-400/50' :
                            leader.rank === 2 ? 'bg-gray-400/30 text-gray-200 border border-gray-400/50' :
                            'bg-orange-400/30 text-orange-200 border border-orange-400/50'
                          }`}>
                            TOP {leader.rank}
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* Epic Footer */}
        <motion.div 
          className="relative z-10 w-full px-4 sm:px-6 mt-4 sm:mt-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="bg-gradient-to-r from-gray-900/80 to-black/60 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-gray-700/50">
            <div className="text-center">
              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4"
                animate={{ 
                  scale: [1, 1.02, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <FaTrophy className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-400" />
                <span className="text-gray-300 font-medium text-sm sm:text-lg text-center px-2">
                  Completa desafíos para ganar puntos y entrar al Top 10 mundial
                </span>
                <FaBolt className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-400" />
              </motion.div>
              
              {/* Anti-Cheating Notice */}
              <motion.div
                className="bg-red-500/10 border border-red-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <FaShieldAlt className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                  <span className="text-red-300 font-bold text-sm sm:text-base">Sistema Anti-Trampa</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-center text-xs sm:text-sm">
                  <div className="text-red-200">
                    <FaBolt className="w-3 h-3 sm:w-4 sm:h-4 mx-auto mb-1" />
                    <div>🇪🇸 Detección automática de trampas</div>
                  </div>
                  <div className="text-red-200">
                    <FaBolt className="w-3 h-3 sm:w-4 sm:h-4 mx-auto mb-1" />
                    <div>🇺🇸 Automatic cheat detection</div>
                  </div>
                  <div className="text-red-200">
                    <FaLightbulb className="w-3 h-3 sm:w-4 sm:h-4 mx-auto mb-1" />
                    <div>🇫🇷 Détection automatique de triche</div>
                  </div>
                </div>
              </motion.div>
              
              {/* Animated Dots */}
              <div className="flex justify-center space-x-1 sm:space-x-2">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-400/60 rounded-full"
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.4, 1, 0.4]
                    }}
                    transition={{ 
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Custom Styles */}
        <style>{`
          .bg-grid-pattern {
            background-image: 
              linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
            background-size: 40px 40px;
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }

          @keyframes glow {
            0%, 100% {
              box-shadow: 0 0 15px rgba(251, 191, 36, 0.3);
            }
            50% {
              box-shadow: 0 0 25px rgba(251, 191, 36, 0.5);
            }
          }

          /* Reduce motion for users who prefer it */
          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }

          /* Mobile optimizations */
          @media (max-width: 640px) {
            .backdrop-blur-xl {
              backdrop-filter: blur(8px);
            }
            
            button {
              min-height: 44px;
              min-width: 44px;
            }
            
            /* Ensure text is readable on small screens */
            .text-xs {
              font-size: 0.7rem;
              line-height: 1rem;
            }
            
            /* Compact spacing for mobile */
            .space-y-2 > * + * {
              margin-top: 0.25rem;
            }
            
            /* Better touch targets */
            .touch-target {
              min-height: 44px;
              min-width: 44px;
            }
            
            /* Prevent horizontal scroll */
            .overflow-x-hidden {
              overflow-x: hidden;
            }
          }

          /* Extra small screens */
          @media (max-width: 480px) {
            .text-sm {
              font-size: 0.75rem;
            }
            
            .p-2 {
              padding: 0.375rem;
            }
            
            .px-2 {
              padding-left: 0.375rem;
              padding-right: 0.375rem;
            }
            
            .py-1 {
              padding-top: 0.25rem;
              padding-bottom: 0.25rem;
            }
          }

          /* Improve touch responsiveness */
          @media (hover: none) and (pointer: coarse) {
            .hover\\:scale-\\[1\\.02\\]:hover {
              transform: none;
            }
            
            .hover\\:scale-\\[1\\.02\\]:active {
              transform: scale(0.98);
            }
            
            /* Better touch feedback */
            .active\\:scale-95:active {
              transform: scale(0.95);
            }
          }
        `}</style>
      </div>
    </div>
  );
}